import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
	UseQueryResult,
	UseMutationResult,
} from "@tanstack/react-query";
import {
	getComplianceQuestions,
	saveComplianceQuestions,
	skipCompliance,
	startCompliance,
	getComplianceGrid,
	saveComplianceGrid,
	getComplianceStatus,
} from "../api/compliance-api";
import type {
	ComplianceQuestionsResponse,
	SaveComplianceQuestionsRequest,
	ComplianceGrid,
	SaveComplianceGridRequest,
	ComplianceStatus,
} from "../types/compliance";

const STALE_TIME = 30_000;

export const complianceKeys = {
	root: (rfxId: number) => ["compliance", rfxId] as const,
	questions: (rfxId: number) => ["compliance", rfxId, "questions"] as const,
	grid: (rfxId: number) => ["compliance", rfxId, "grid"] as const,
	status: (rfxId: number) => ["compliance", rfxId, "status"] as const,
};

/** GET /rfx/{rfxId}/evaluation/compliance/questions (contract row 6). */
export const useComplianceQuestions = (
	rfxId: number | undefined
): UseQueryResult<ComplianceQuestionsResponse> =>
	useQuery({
		queryKey: complianceKeys.questions(rfxId!),
		queryFn: () => getComplianceQuestions(rfxId!),
		enabled: !!rfxId,
		staleTime: STALE_TIME,
	});

/** PUT questions (contract row 7). Invalidates the questions cache on success. */
export const useSaveComplianceQuestions = (
	rfxId: number | undefined
): UseMutationResult<
	ComplianceQuestionsResponse,
	Error,
	SaveComplianceQuestionsRequest
> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: SaveComplianceQuestionsRequest) =>
			saveComplianceQuestions(rfxId!, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: complianceKeys.questions(rfxId!),
			});
		},
	});
};

/** POST skip (contract row 10). Invalidates the status cache on success. */
export const useSkipCompliance = (
	rfxId: number | undefined
): UseMutationResult<void, Error, void> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => skipCompliance(rfxId!),
		onSuccess: () => {
			// Skip seals the whole compliance flow (compliance_skipped=1 +
			// is_evaluated=1 on submitted invites), so invalidate every compliance
			// query for this rfx (status, questions, grid) — not just status.
			queryClient.invalidateQueries({
				queryKey: complianceKeys.root(rfxId!),
			});
		},
	});
};

/** POST start — re-open Pre-Compliance after a skip. Invalidates all compliance
 *  queries for this rfx (the skip's evaluated/answers state has been reset). */
export const useStartCompliance = (
	rfxId: number | undefined
): UseMutationResult<void, Error, void> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => startCompliance(rfxId!),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: complianceKeys.root(rfxId!),
			});
		},
	});
};

/** GET grid (Task 009). */
export const useComplianceGrid = (
	rfxId: number | undefined
): UseQueryResult<ComplianceGrid> =>
	useQuery({
		queryKey: complianceKeys.grid(rfxId!),
		queryFn: () => getComplianceGrid(rfxId!),
		enabled: !!rfxId,
		staleTime: STALE_TIME,
	});

/** PUT grid (Task 009). Invalidates the grid and status caches on success. */
export const useSaveComplianceGrid = (
	rfxId: number | undefined
): UseMutationResult<ComplianceGrid, Error, SaveComplianceGridRequest> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: SaveComplianceGridRequest) => saveComplianceGrid(rfxId!, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: complianceKeys.grid(rfxId!),
			});
			queryClient.invalidateQueries({
				queryKey: complianceKeys.status(rfxId!),
			});
		},
	});
};

/** GET status (contract row 11; Task 010 consumes this). */
export const useComplianceStatus = (
	rfxId: number | undefined
): UseQueryResult<ComplianceStatus> =>
	useQuery({
		queryKey: complianceKeys.status(rfxId!),
		queryFn: () => getComplianceStatus(rfxId!),
		enabled: !!rfxId,
		staleTime: STALE_TIME,
	});
